import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CreditCard, Loader2 } from 'lucide-react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
  });

  const subtotal = getCartTotal();
  const shipping = subtotal > 2000 ? 0 : 100;
  const total = subtotal + shipping;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.address) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setLoading(false);
        return;
      }

      // Create order
      const orderData = {
        amount: total,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        items: cart.map((item) => ({
          product_id: item.id,
          name: item.name,
          quantity: item.quantity,
          size: item.size,
          price: item.price,
        })),
        shipping_address: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
        },
      };

      const response = await api.post('/orders/create', orderData);
      const { order_id, amount, currency, key_id } = response.data;

      // Razorpay options
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        order_id: order_id,
        name: 'Rudra Apparel',
        description: 'Order Payment',
        handler: async (razorpayResponse) => {
          try {
            // Verify payment
            await api.post('/orders/verify', {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
            });

            toast.success('Payment successful!');
            clearCart();
            navigate('/order-success');
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: '#091C2A',
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        toast.error('Payment failed. Please try again.');
        console.error('Payment failed:', response.error);
      });
      razorpay.open();
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="checkout-empty">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-brand-pearl mb-4">Your cart is empty</h2>
          <button onClick={() => navigate('/shop')} className="text-brand-walnut hover:text-brand-pearl">
            Go to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="checkout-page">
      <section className="section-spacing">
        <div className="container-custom max-w-5xl">
          <h1 className="text-4xl font-heading font-bold text-brand-pearl mb-8" data-testid="checkout-heading">
            Checkout
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handlePayment}
                className="bg-brand-ivory rounded-sm border border-brand-pearl/10 p-8"
                data-testid="checkout-form"
              >
                <h2 className="text-2xl font-heading font-bold text-brand-pearl mb-6">Shipping Details</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="name">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="checkout-name"
                      className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="email">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="checkout-email"
                        className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="phone">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="checkout-phone"
                        className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="address">
                      Address *
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      data-testid="checkout-address"
                      rows="3"
                      className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="city">
                        City
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        data-testid="checkout-city"
                        className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="state">
                        State
                      </label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        data-testid="checkout-state"
                        className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="pincode">
                        Pincode
                      </label>
                      <input
                        type="text"
                        id="pincode"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        data-testid="checkout-pincode"
                        className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="pay-now-btn"
                  className="w-full mt-8 flex items-center justify-center px-8 py-4 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 w-5 h-5" />
                      Pay ₹{total.toLocaleString()}
                    </>
                  )}
                </button>
              </motion.form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-brand-timberwolf rounded-sm border border-brand-pearl/10 p-6">
                  <h2 className="text-xl font-heading font-bold text-brand-pearl mb-4">Order Summary</h2>

                  <div className="space-y-3 mb-6">
                    {cart.map((item) => (
                      <div key={`${item.id}-${item.size}`} className="flex justify-between text-sm text-brand-walnut">
                        <span>
                          {item.name} ({item.size}) x {item.quantity}
                        </span>
                        <span>₹{(item.price * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2 pt-4 border-t border-brand-pearl/10">
                    <div className="flex justify-between text-brand-walnut">
                      <span>Subtotal</span>
                      <span data-testid="summary-subtotal">₹{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-brand-walnut">
                      <span>Shipping</span>
                      <span data-testid="summary-shipping">{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
                    </div>
                  </div>

                  <div className="flex justify-between text-xl font-bold text-brand-pearl mt-4 pt-4 border-t border-brand-pearl/10">
                    <span>Total</span>
                    <span data-testid="summary-total">₹{total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CheckoutPage;